import * as React from "react";
import { WithStyles, withStyles, createStyles, Theme } from 'components/controls/styles';
import { withActionsExecutor, ActionsExecutorPropsType } from "components/actions";
import ConfirmDialog from "common/components/controls/ConfirmDialog";
import Loading from "common/components/controls/Loading";
import Error from "common/components/controls/Error";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Typography } from "components/controls/widget";
import CloseIcon from '@material-ui/icons/Close';
import EditacePlatbyForm, { Platba, PopisPlatby } from "./EditacePlatbyForm";
import { Payment, PaymentItem, PaymentState, PaymentDisposition, apiGetPaymentAvailable, apiPostPayment, apiPutPayment, PaymentSpec } from "common/api/Payments";


interface EditacePlatbyProps {
    show: boolean;
    idtClena: number;
    rok: number;
    jenPolovicniPlatby: boolean;
    onClose: (p?: Payment) => void;
}

interface State {
    loading: boolean;
    hasError: boolean;
    error?: string;
    showConfirmDialog: boolean;
    saving: boolean;
    hasSaveError: boolean;
    saveError?: string;
    payment?: Payment;
    platba?: Platba;
    platbaCelkem: number;
}

type PropsType = EditacePlatbyProps & State & ActionsExecutorPropsType & WithStyles<typeof styles>;

const initialState: State = {
    loading: true,
    hasError: false,
    showConfirmDialog: false,
    saving: false,
    hasSaveError: false,
    platbaCelkem: 0,
}


class EditacePlatby extends React.PureComponent<PropsType> {

    constructor(props: PropsType) {
        super(props);
    }

    load = (idtClena: number, rok: number) => {

        const { executeAction, updateState } = this.props;

        const action = apiGetPaymentAvailable(idtClena, rok)
            .onSuccess(d => {
                const platba = this.getPlatba(d);
                const platbaCelkem = this.sumaPoplatku(platba, d);
                updateState({ loading: false, payment: d, platba: platba, platbaCelkem: platbaCelkem });
            })
            .onError(e => {
                const errMessage = e.message;
                updateState({ loading: false, hasError: true, error: errMessage });
            });

        executeAction(action, { loading: true, hasError: false, payment: undefined });
    }

    save = (completed: boolean) => {

        const { executeAction, updateState, payment, platba, idtClena } = this.props;

        const action = payment && payment.id ? apiPutPayment(idtClena, payment.id) : apiPostPayment(idtClena);
        action
            .onSuccess(d => {
                updateState({ saving: false });
                this.props.onClose(d);
            })
            .onError(e => {
                const errMessage = e.message;
                updateState({ saving: false, hasSaveError: true, saveError: errMessage });
            });

        const paymentData = this.getPaymentData(payment, platba, completed);

        executeAction(action, { saving: true, hasSaveError: false, saveError: undefined }, paymentData);
    }

    getPaymentData = (payment: Payment, pba: Platba, completed: boolean) => {
        const p: PaymentItem = {
            memberId: payment.ownerId,
        };


        if (pba.sp.checked) {
            p.sp = {
                selected: true,
            }
        }

        if (pba.zcp.checked) {
            p.zcp = {
                selected: true,
                half: pba.zcp.polovicni
            }
        }

        if (pba.soutAm.checked) {
            p.rcp = {
                selected: true,
                half: pba.soutAm.polovicni,
            }
        }

        if (pba.soutPro.checked) {
            p.rcp = {
                selected: true,
                half: pba.soutPro.polovicni,
                profi: true,
            }
        }

        if (pba.por.checked) {
            p.por = {
                selected: true,
                selectedClass: pba.por.trida,
            }
        }

        if (pba.tre.checked) {
            p.tre = {
                selected: true,
                selectedClass: pba.tre.trida,
            }
        }

        if (pba.fun.checked) {
            p.fun = {
                selected: true,
                selectedClass: pba.fun.trida,
            }
        }



        const newPayment: Payment = {
            id: payment.id,
            ownerId: payment.ownerId,
            year: payment.year,
            state: completed ? PaymentState.Sended : PaymentState.Created,
            items: [p],
        };

        return newPayment;
    }

    submit = (completed: boolean) => {
        if (completed) {
            this.props.updateState({ showConfirmDialog: true })
        } else {
            this.save(false);
        }
    }

    cancel = () => {
        this.props.onClose();
    }

    onEnter = () => {
        const { idtClena, rok } = this.props;
        this.load(idtClena, rok);
    }

    handleConfirm = (confirm: boolean) => {

        this.props.updateState({ showConfirmDialog: false });

        if (confirm) {
            this.save(true);
        }
    }

    getConfirmDialogContent = () => {
        return <div>
            <Typography variant="h6">{'Opravdu chcete platbu odeslat ke zpracování?'}</Typography>
            <Typography variant="body1">{'Po odeslání již nebudete moci platbu změnit.'}</Typography>
        </div>
    }

    setPopisPlatby = (sp: PaymentSpec, pp: PopisPlatby) => {

        if (sp.disposition === PaymentDisposition.Required) {
            pp.musiPlatit = true;
            pp.checked = true;
        }

        if (sp.disposition === PaymentDisposition.None) {
            pp.neplatit = true;
        }

        if (sp.disposition === PaymentDisposition.Paid) {
            pp.jizZaplaceno = true;
        }
    }

    getPlatba = (payment: Payment) => {
        const p = payment.items[0];
        const pba: Platba = {
            sp: {},
            zcp: {},
            soutAm: {},
            soutPro: {},
            por: {},
            fun: {},
            tre: {},
        }

        this.setPopisPlatby(p.sp, pba.sp);

        this.setPopisPlatby(p.zcp, pba.zcp);
        if (p.zcp.disposition === PaymentDisposition.Required) {
            pba.zcp.polovicni = this.props.jenPolovicniPlatby;
        }

        pba.soutAm.polovicni = this.props.jenPolovicniPlatby;
        pba.soutPro.polovicni = this.props.jenPolovicniPlatby;

        this.setPopisPlatby(p.rcp, pba.soutAm);
        this.setPopisPlatby(p.rcp, pba.soutPro);

        if (pba.soutAm.jizZaplaceno || pba.soutPro.jizZaplaceno) {
            pba.soutAm.hidden = p.rcp.profi === true;
            pba.soutPro.hidden = p.rcp.profi !== true;
        }

        if (p.rcp.disposition == PaymentDisposition.Optional) {
            if (p.rcp.profi) {
                pba.soutPro.checked = p.rcp.selected;
                pba.soutPro.polovicni = p.rcp.half || this.props.jenPolovicniPlatby;

            } else {
                pba.soutAm.checked = p.rcp.selected;
                pba.soutAm.polovicni = p.rcp.half || this.props.jenPolovicniPlatby;
            }
        }


        this.setPopisPlatby(p.por, pba.por);

        if (p.por.disposition == PaymentDisposition.Optional) {
            pba.por.trida = p.por.selectedClass || 1;
            pba.por.checked = p.por.selected;
        }

        this.setPopisPlatby(p.tre, pba.tre);

        if (p.tre.disposition == PaymentDisposition.Optional) {
            pba.tre.trida = p.tre.selectedClass || 1;
            pba.tre.checked = p.tre.selected;
        }

        this.setPopisPlatby(p.fun, pba.fun);

        if (p.fun.disposition == PaymentDisposition.Optional) {
            pba.fun.trida = p.fun.selectedClass || 1;
            pba.fun.checked = p.fun.selected;
        }



        return pba;
    }

    updatePlatby = (novaPlatba: Platba) => {

        const { platba, payment } = this.props;

        if (platba.soutAm.checked !== novaPlatba.soutAm.checked) {
            novaPlatba.soutPro.checked = false;
            novaPlatba.soutAm.polovicni = platba.soutPro.polovicni;
        }

        if (platba.soutPro.checked !== novaPlatba.soutPro.checked) {
            novaPlatba.soutAm.checked = false;
            novaPlatba.soutPro.polovicni = platba.soutAm.polovicni;
        }

        const platbaCelkem = this.sumaPoplatku(novaPlatba, payment);

        this.props.updateState({ platba: novaPlatba, platbaCelkem: platbaCelkem });
    }

    getClassPrice = (sp: PaymentSpec, trida: number) => {

        switch (trida) {
            case 3: return sp.priceClass3;
            case 2: return sp.priceClass2;
            default: return sp.priceClass1;
        }
    }

    getSleva = (popl: number, sleva: boolean, extFeeDiscount: number) => {
        return sleva ? popl - extFeeDiscount : popl;
    }

    sumaPoplatku = (pba: Platba, payment: Payment) => {

        const p = payment.items[0];
        p.extFeeDiscount
        let narokNaSlevu = false
            || pba.soutAm.jizZaplaceno === true
            || pba.soutPro.jizZaplaceno === true
            || pba.por.jizZaplaceno === true
            || pba.fun.jizZaplaceno === true
            || pba.tre.jizZaplaceno === true;

        if (pba.sp.checked) {
            pba.sp.castka = p.sp.price;
        } else {
            pba.sp.castka = 0;
        }

        if (pba.zcp.checked) {
            pba.zcp.castka = (pba.zcp.polovicni ? p.zcp.halfPrice : p.zcp.price);
        } else {
            pba.zcp.castka = 0;
        }

        if (pba.soutAm.checked) {
            const popl = (pba.soutAm.polovicni ? p.rcp.halfPrice : p.rcp.price);
            pba.soutAm.castka = this.getSleva(popl, narokNaSlevu, p.extFeeDiscount);
            narokNaSlevu = true;
        } else {
            pba.soutAm.castka = 0;
        }

        if (pba.soutPro.checked) {
            const popl = (pba.soutPro.polovicni ? p.rcp.profiHalfPrice : p.rcp.profiPrice);
            pba.soutPro.castka = this.getSleva(popl, narokNaSlevu, p.extFeeDiscount);
            narokNaSlevu = true;
        } else {
            pba.soutPro.castka = 0;
        }

        if (pba.por.checked) {
            const popl = this.getClassPrice(p.por, pba.por.trida);
            pba.por.castka = this.getSleva(popl, narokNaSlevu, p.extFeeDiscount);
            narokNaSlevu = true;
        } else {
            pba.por.castka = 0;
        }

        if (pba.tre.checked) {
            const popl = this.getClassPrice(p.tre, pba.tre.trida);
            pba.tre.castka = this.getSleva(popl, narokNaSlevu, p.extFeeDiscount);
            narokNaSlevu = true;
        } else {
            pba.tre.castka = 0;
        }

        if (pba.fun.checked) {
            const popl = this.getClassPrice(p.fun, pba.fun.trida);
            pba.fun.castka = this.getSleva(popl, narokNaSlevu, p.extFeeDiscount);
            narokNaSlevu = true;
        } else {
            pba.fun.castka = 0;
        }

        return pba.sp.castka + pba.zcp.castka + pba.soutAm.castka + pba.soutPro.castka + pba.por.castka + pba.fun.castka + pba.tre.castka;
    }

    render() {
        const { classes, rok, loading, hasError, error, show, saving, hasSaveError, saveError, showConfirmDialog, platba, jenPolovicniPlatby, platbaCelkem } = this.props;

        const disableButtons = saving || loading || hasError || platbaCelkem === 0;

        return <Dialog open={show} fullWidth maxWidth="md" onEnter={this.onEnter}>
            <DialogTitle disableTypography className={classes.titleRoot}>
                <Typography variant="h6">{`Editace platby příspěvků ${rok}`}</Typography>
                <IconButton aria-label="Close" className={classes.titleCloseButton} onClick={this.cancel} disabled={saving}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.contentRoot}>
                {loading
                    ? <Loading loading={loading} color="primary" />
                    : hasError
                        ? <Error>{error}</Error>
                        : <EditacePlatbyForm platba={platba} updatePlatby={this.updatePlatby} platbaCelkem={platbaCelkem} jenPolovicniPlatby={jenPolovicniPlatby} />
                }
            </DialogContent>
            <DialogActions className={classes.actionsRoot}>
                <Grid container direction="column" spacing={8}>
                    {hasSaveError && <Grid item>
                        <Error noPadding>{saveError}</Error>
                    </Grid>}
                    <Grid item>

                        <Grid container direction="row" spacing={8}>
                            <Grid item>
                                <Button type="submit" variant="contained" color="primary" disabled={disableButtons} onClick={e => this.submit(false)}>{'Uložit a jít zpět'}</Button>
                            </Grid>
                            <Grid item>
                                <Button type="submit" variant="contained" color="success" disabled={disableButtons} onClick={e => this.submit(true)}>{'Odeslat ke zpracování'}</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogActions>
            <ConfirmDialog show={showConfirmDialog} title="Odeslat ke zpracování" content={this.getConfirmDialogContent} onResult={this.handleConfirm} />
        </Dialog>;
    }
}


const styles = (theme: Theme) => createStyles({
    titleRoot: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        margin: 0,
        padding: theme.spacing.unit * 2,
    },
    titleCloseButton: {
        position: 'absolute',
        right: theme.spacing.unit,
        top: theme.spacing.unit,
        color: theme.palette.grey[500],
    },
    contentRoot: {
        paddingTop: theme.spacing.unit * 2,
    },
    actionsRoot: {
        borderTop: `1px solid ${theme.palette.divider}`,
        margin: 0,
        padding: theme.spacing.unit,
    },
});

export default withActionsExecutor<EditacePlatbyProps, State>(initialState, withStyles(styles)(EditacePlatby));


